/**
 * Route-level Error Boundary
 * Isolates errors to specific routes without crashing the entire app
 */
import React, { Component, ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  routeName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to console in development
    if (import.meta.env.DEV) {
      console.error('[Route Error Boundary]', error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });

    // TODO: Send error to error logging service (e.g., Sentry)
    // logErrorToService(error, errorInfo, this.props.routeName);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div
          style={{
            padding: '40px 20px',
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ color: '#d32f2f', marginBottom: '12px' }}>
            {this.props.routeName
              ? `Error in ${this.props.routeName}`
              : 'Something went wrong on this page'}
          </h2>
          <p style={{ color: '#666', marginBottom: '24px', lineHeight: '1.6' }}>
            An unexpected error occurred. The rest of the application is still working.
          </p>

          <div
            style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '32px' }}
          >
            <button
              onClick={this.handleReset}
              style={{
                padding: '12px 24px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
              }}
            >
              Try Again
            </button>

            <Link
              to="/"
              style={{
                padding: '12px 24px',
                backgroundColor: '#fff',
                color: '#1976d2',
                border: '2px solid #1976d2',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              Go to Dashboard
            </Link>
          </div>

          {/* Show error details in development */}
          {import.meta.env.DEV && this.state.error && (
            <details style={{ marginTop: '32px', textAlign: 'left' }}>
              <summary
                style={{
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  marginBottom: '12px',
                  color: '#666',
                }}
              >
                Error Details (Development Only)
              </summary>
              <div
                style={{
                  background: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              >
                <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre
                    style={{
                      fontSize: '12px',
                      color: '#666',
                      whiteSpace: 'pre-wrap',
                      margin: 0,
                      overflowX: 'auto',
                    }}
                  >
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
